import {
  ATTR_HTTP_RESPONSE_STATUS_CODE,
  ATTR_HTTP_ROUTE,
} from '@opentelemetry/semantic-conventions';
import * as Sentry from '@sentry/nextjs';
import { personPageLink } from 'components/cards/links';
import Layout from 'components/layout';
import PersonPage from 'components/pages/person/PersonPage';
import { getOfficialLink } from 'lib/api/officialLinks';
import { getPetitionsForRep } from 'lib/api/themis/queries';
import { getLettersToRep } from 'lib/api/themis/queries/letters';
import {
  getPollForRep,
  getPollVotesForRep,
} from 'lib/api/themis/queries/polls';
import { getDeliveryMetricsForBioguide } from 'lib/api/themis/queries/repTotals';
import { DISCOVERY_ENDPOINTS, discovery } from 'lib/discovery';
import { otelApi } from 'lib/instrumentation'; // Initializes instrumentation
import PropTypes from 'prop-types';
import { getOpenGraphUrl } from 'utilities/paths';
import { conditionallyFormatName } from 'utilities/people';
import { hasSufficientPollData } from 'utilities/polls';
import { CampaignTypeList, OpenLetterTypeList } from 'utilities/propTypes';
import { getStateAPAbbreviation } from 'utilities/states';
import {
  getLongTargetTypeFromShort,
  isCongressBioguide,
} from 'utilities/targets';

const REVALIDATE_TIME_MINUTES = 5;
const REVALIDATE_TIME_SECONDS = REVALIDATE_TIME_MINUTES * 60;
const DATA_DAYS_WINDOW = 30;
const DATA_DAYS_PER_POINT = 1;

const Person = ({ petitions, openLetters, person }) => {
  // Dynamic OG image
  const fullName = person.fullName;
  const target = person.target;
  const state = person.stateCode;
  const city = person.city;
  const officialType = person.officialType || person.target || '';

  const dynamicOpenGraphUrlHref = getOpenGraphUrl('/api/og/person/person', {
    fullName,
    target,
    state,
    city,
    bioguideId: person.bioguideId,
    officialType,
  });

  const hasState = !!state;
  const hasCity = !!city;
  let locationDetail = '';
  const targetDetail = target
    ? getLongTargetTypeFromShort(target)
    : 'Official Inbox';

  if (hasState && hasCity) {
    locationDetail = `, ${city}, ${getStateAPAbbreviation(state)}`;
  } else if (hasState && !hasCity) {
    locationDetail = `, ${getStateAPAbbreviation(state)}`;
  }

  const imageAltText = `A shareable card that reads "${person.fullName}${locationDetail} followed by "text ${targetDetail} to 50409"`;

  return (
    <Layout
      title={`${person.fullName} — Letters & Petitions | Resistbot`}
      description={`All the letters delivered to ${person.fullName} via Resistbot campaigns.`}
      dynamicImage={dynamicOpenGraphUrlHref}
      imageAltText={imageAltText}
      canonicalPath={personPageLink({
        bioguideId: person.bioguideId,
        fullName: person.fullNameSlug,
      })}
    >
      <PersonPage
        person={person}
        petitions={petitions}
        openLetters={openLetters}
      />
    </Layout>
  );
};

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps = Sentry.wrapGetStaticPropsWithSentry(
  async ({ params }) => {
    const parentCtx = otelApi.context.active();
    const parentSpan = otelApi.trace
      .getTracer('Gin Rickey')
      .startSpan('content-for-person', undefined, parentCtx);

    // Ideally we' have some way to fetch point-in-time data for a person, but Disco is real-time
    const { bioguideId } = params;
    const personName = params.all[0];

    parentSpan.setAttributes({
      [ATTR_HTTP_ROUTE]: `/explore/person/${bioguideId}/${personName}`,
      'person.name': personName,
      'person.id': bioguideId,
    });

    const results = Promise.all([
      getPetitionsForRep({ bioguideId }),
      getLettersToRep({ bioguideId }),
      getDeliveryMetricsForBioguide({
        bioguideId,
        chartPeriodDays: DATA_DAYS_PER_POINT,
        chartSpanDays: DATA_DAYS_WINDOW,
      }),
      getPollForRep({ bioguideId }),
      getPollVotesForRep({ bioguideId }),
      discovery.get(DISCOVERY_ENDPOINTS.V2.OFFICIAL, {
        bio_id: bioguideId,
      }),
      getOfficialLink(bioguideId),
    ]);

    const [
      petitionsResults,
      lettersResults,
      repTotalResults,
      pollResults,
      pollVotes,
      discoveryPersonResult,
      officialLink,
    ] = await results;
    const { petitions, person: petitionPerson } = petitionsResults;
    const letters = lettersResults;
    const { repTotal, deliveriesData } = repTotalResults;
    const person = discoveryPersonResult[0] || null; // This call returns arrays but will only be one with a bioguideId or []

    const lettersForList = letters.sort(
      (a, b) => b.sentTimestamp - a.sentTimestamp
    );

    // Disco may 'forget' about them ever time, since it's only point in time
    // but we might have letters / petitions / poll data for them.
    // However some reps may have no data present
    const hasPetitionPerson =
      petitionPerson && Object.keys(petitionPerson).length > 0;

    const repExists =
      !!person ||
      !!hasPetitionPerson ||
      !!letters.length ||
      !!petitions.length ||
      !!pollResults;

    if (!repExists) {
      parentSpan.setAttributes({
        [ATTR_HTTP_RESPONSE_STATUS_CODE]: 404,
      });

      parentSpan.end();
      return {
        notFound: true,
        revalidate: REVALIDATE_TIME_SECONDS * 4, // Can wait longer than usual
      };
    }

    const personNameHuman = conditionallyFormatName(person, petitionPerson);

    const hasSufficientData = hasSufficientPollData({
      up: pollResults?.up || 0,
      down: pollResults?.down || 0,
    });

    const personData = {
      bioguideId,
      titleAbbreviation: person?.title_abbrev || '',
      fullName: personNameHuman,
      fullNameSlug: petitionPerson?.fullName || personNameHuman, // The way petitions are is better for urls, matches legacy.
      target: person?.rep_type || '',
      city: person?.rep_city || '',
      stateCode:
        person?.state ||
        letters[0]?.district?.state ||
        petitionPerson?.state ||
        '',
      isFederalTarget: isCongressBioguide(bioguideId),
      delivery: {
        chart: deliveriesData || [],
        total: repTotal,
        timeWindowDays: DATA_DAYS_WINDOW,
      },
      approval: {
        // If a poll exists, use the approval rating, otherwise null to assert it does not exist (0/null test similarly)
        rating: pollResults ? pollResults.approval : null,
        hasSufficientData,
        pollVotes: pollVotes,
      },
      amendmentCandidate: officialLink || null,
    };

    parentSpan.end();
    return {
      props: {
        petitions: petitions,
        openLetters: lettersForList,
        person: personData,
      },
      revalidate: REVALIDATE_TIME_SECONDS,
    };
  }
);

Person.propTypes = {
  petitions: PropTypes.arrayOf(CampaignTypeList),
  openLetters: PropTypes.arrayOf(OpenLetterTypeList),
  person: PropTypes.shape({
    bioguideId: PropTypes.string.isRequired,
    titleAbbreviation: PropTypes.string,
    fullName: PropTypes.string.isRequired,
    fullNameSlug: PropTypes.string.isRequired,
    stateCode: PropTypes.string.isRequired,
    target: PropTypes.string.isRequired,
    city: PropTypes.string,
    isFederalTarget: PropTypes.bool.isRequired,
    delivery: PropTypes.shape({
      chart: PropTypes.arrayOf(PropTypes.number),
      total: PropTypes.number,
      timeWindowDays: PropTypes.number,
    }),
    approval: PropTypes.shape({
      rating: PropTypes.number,
      hasSufficientData: PropTypes.bool.isRequired,
      pollVotes: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          userId: PropTypes.string,
          pollId: PropTypes.string,
          city: PropTypes.string,
          state: PropTypes.string,
          direction: PropTypes.string,
          verifiedVoter: PropTypes.bool,
          reason: PropTypes.string,
          created: PropTypes.string,
        })
      ),
    }),
    amendmentCandidate: PropTypes.shape({
      url: PropTypes.string.isRequired,
    }),
  }).isRequired,
};

export default Person;
